import { NavigateOptions } from "react-router";
import { getActionUrl } from "../../app/draftHelper";
import { UserRole } from "../../app/userSlice";
import { WritingAction } from "../writing/writingApiSlice";
import { NotificationBaseViewModel, NotificationTypes } from "./notificationsApiSlice";

export interface HandleSelectNotificationCallbacks {
	setNotificationId: (id: number) => void;
	setNotificationUrl: (url: string | undefined) => void;
	setNotificationUrlOptions: (options: NavigateOptions | undefined) => void;
	callGetDraftAction: (draftId: number) => Promise<{ action?: WritingAction }>;
	callGetDraftPromptId: (draftId: number) => Promise<{ promptId: number }>;
}

export function handleSelectNotification(
	notification: NotificationBaseViewModel,
	role: UserRole,
	callbacks: HandleSelectNotificationCallbacks
) {
	const {
		setNotificationId,
		setNotificationUrl,
		setNotificationUrlOptions,
		callGetDraftAction,
		callGetDraftPromptId,
	} = callbacks;

	setNotificationId(notification.id);

	if (role === UserRole.Writer) {
		if (notification.notificationType == NotificationTypes.NewMessage) {
			getDraftInfo(notification.lastDraftId, setNotificationUrl, callGetDraftAction);
			setNotificationUrlOptions(getNotificationOptions(notification));
		} else if (notification.notificationType == NotificationTypes.DraftComment) {
			getDraftInfo(notification.draftId, setNotificationUrl, callGetDraftAction);
		} else {
			setNotificationUrl(getNotificationUrl(notification));
			setNotificationUrlOptions(getNotificationOptions(notification));
		}
	} else {
		if (notification.notificationType == NotificationTypes.NewMessage) {
			getDraftPromptId(
				notification.lastDraftId,
				notification,
				setNotificationUrl,
				callGetDraftPromptId
			);
		} else if (
			notification.notificationType == NotificationTypes.DraftFinalize ||
			notification.notificationType == NotificationTypes.DraftSubmit
		) {
			getDraftPromptId(
				notification.draftId,
				notification,
				setNotificationUrl,
				callGetDraftPromptId
			);
		} else {
			setNotificationUrl(getNotificationUrl(notification));
		}
		setNotificationUrlOptions(getNotificationOptions(notification));
	}
}

export function getNotificationUrl(notification: NotificationBaseViewModel) {
	switch (notification.notificationType) {
		case NotificationTypes.LessonAssigned:
			return `/lessons/${notification.lessonCodeName}`;
		case NotificationTypes.DueDate:
			return `/writing?promptId=${notification.promptId}`;
		case NotificationTypes.DueDateExceeded:
			return `/writing`;
		case NotificationTypes.PeerReview:
		case NotificationTypes.PeerReviewRequest:
			return `/reviews/${notification.draftPeerReviewId}`;
		case NotificationTypes.DraftFinalize:
		case NotificationTypes.DraftSubmit:
			return `/scoreReport?draftId=${notification.draftId}`;
		case NotificationTypes.NewMessage:
			return `/scoreReport?draftId=${notification.lastDraftId}`;
	}
	return `/home`;
}

export function getNotificationOptions(notification: NotificationBaseViewModel) {
	switch (notification.notificationType) {
		case NotificationTypes.DueDateExceeded:
			return {
				state: { expandedStatus: "not started", promptId: notification.promptId },
			};
		case NotificationTypes.NewMessage:
			return {
				state: { section: "EssayMessages" },
			};
	}
	return undefined;
}

function getDraftInfo(
	draftId: number | undefined,
	setNotificationUrl: (url: string | undefined) => void,
	callGetDraftAction: (draftId: number) => Promise<{ action?: WritingAction }>
) {
	if (draftId) {
		callGetDraftAction(draftId)
			.then((data) => {
				if (data.action) {
					const actionUrl = getActionUrl(data.action, draftId);
					setNotificationUrl(actionUrl ?? undefined);
				}
			})
			.catch((error) => {
				console.log(error);
			});
	}
}

function getDraftPromptId(
	draftId: number | undefined,
	notification: NotificationBaseViewModel,
	setNotificationUrl: (url: string | undefined) => void,
	callGetDraftPromptId: (draftId: number) => Promise<{ promptId: number }>
) {
	if (draftId) {
		callGetDraftPromptId(draftId)
			.then((data) => {
				setNotificationUrl(getNotificationUrl(notification) + `&promptId=${data.promptId}`);
			})
			.catch((error) => {
				console.log(error);
			});
	}
}
